import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EyeIcon } from 'lucide-react'

import { HAIRDRESSING_SERVICES } from '@/components/additional-services'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Label } from '@/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { serviceTypeColors } from '@/shared/utils/serviceTypeColors'
import { HairSalonTask } from '@monorepo/functions/src/types/reservations'
import { HairdressingServiceType } from '@monorepo/functions/src/types/services'

import { isTaskUnscheduled } from '../model/task-utils'

interface TaskEditionModalProps {
    isOpen: boolean
    onClose: () => void
    onSave: (hourWithMinutes: string, service: HairdressingServiceType, duration: number) => void
    onDelete: () => void
    onGoToReservation?: () => void
    hour: string
    task: HairSalonTask
}

export function TaskEditionModal({
    isOpen,
    onClose,
    onSave,
    onDelete,
    onGoToReservation,
    hour,
    task,
}: TaskEditionModalProps) {
    const { t } = useTranslation()
    const minutes = [0, 15, 30, 45]
    const durations = [15, 30, 45, 60, 75, 90]

    const [selectedService, setSelectedService] = useState<HairdressingServiceType | ''>(task.service)
    const [selectedDuration, setSelectedDuration] = useState<string>(
        isTaskUnscheduled(task) ? '60' : task.duration.toString(),
    )
    const [deleteConfirm, setDeleteConfirm] = useState(false)

    const getInitialMinute = () => {
        if (isTaskUnscheduled(task)) return 0
        if ('time' in task && typeof task.time === 'string') {
            const parts = task.time.split(':')
            if (parts.length === 2) {
                return parseInt(parts[1], 10) || 0
            }
        }
        return 0
    }

    const [selectedMinute, setSelectedMinute] = useState<number>(getInitialMinute())

    useEffect(() => {
        setSelectedService(task.service)
        setSelectedMinute(getInitialMinute())
        setDeleteConfirm(false)
    }, [task])

    const handleSave = () => {
        if (!selectedService) return
        onSave(`${hour}:${selectedMinute.toString().padStart(2, '0')}`, selectedService, parseInt(selectedDuration))
        setDeleteConfirm(false)
        onClose()
    }

    // Reset state when modal closes
    const handleClose = () => {
        setDeleteConfirm(false)
        onClose()
    }

    const handleDeleteClick = () => {
        if (!deleteConfirm) {
            setDeleteConfirm(true)
        } else if (onDelete) {
            setDeleteConfirm(false)
            onDelete()
        }
    }

    const handleGoToReservation = () => {
        onGoToReservation?.()
        onClose()
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className='sm:max-w-[425px]'>
                <DialogHeader>
                    <DialogTitle className='flex items-center justify-start gap-2'>
                        {t('hairSalonCalendar.editTask', 'Editar tarea')}
                        {onGoToReservation && (
                            <Button variant='outline' onClick={handleGoToReservation} type='button' size='sm'>
                                <EyeIcon />
                                {t('hairSalonCalendar.goToReservation', 'Ir a la reserva')}
                            </Button>
                        )}
                    </DialogTitle>
                </DialogHeader>
                <div className='grid gap-4 py-4'>
                    {/* Service selection */}
                    <div className='space-y-2'>
                        <Label>Servicio</Label>
                        <Select
                            value={selectedService}
                            onValueChange={value => setSelectedService(value as HairdressingServiceType)}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder='Selecciona un servicio' />
                            </SelectTrigger>
                            <SelectContent>
                                {HAIRDRESSING_SERVICES.map(service => (
                                    <SelectItem
                                        key={service.value}
                                        value={service.value}
                                        className={serviceTypeColors[service.value]}
                                    >
                                        {t(service.translationKey)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Duration selection */}
                    <div className='space-y-2'>
                        <Label>Duración (minutos)</Label>
                        <Select value={selectedDuration} onValueChange={setSelectedDuration}>
                            <SelectTrigger>
                                <SelectValue placeholder='Selecciona la duración' />
                            </SelectTrigger>
                            <SelectContent>
                                {durations.map(duration => (
                                    <SelectItem key={duration} value={duration.toString()}>
                                        {duration} minutos
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Time selection */}
                    <div className='space-y-2'>
                        <Label>{t('hairSalonCalendar.startTime', 'Hora de inicio')}</Label>
                        <div className='grid grid-cols-2 gap-4'>
                            {minutes.map(minute => (
                                <Button
                                    key={minute}
                                    onClick={() => setSelectedMinute(minute)}
                                    variant={selectedMinute === minute ? 'default' : 'outline'}
                                    className='text-lg'
                                    disabled={!selectedService}
                                >
                                    {hour}:{minute.toString().padStart(2, '0')}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Save, Go to Reservation, and Delete buttons */}
                    <div className='mt-6 flex items-center justify-between gap-2'>
                        <div className='flex gap-2'>
                            {onDelete && !isTaskUnscheduled(task) && (
                                <Button
                                    variant='destructive'
                                    onClick={handleDeleteClick}
                                    type='button'
                                    className={deleteConfirm ? 'animate-pulse' : ''}
                                >
                                    {deleteConfirm
                                        ? t('hairSalonCalendar.confirmDelete', 'Confirmar eliminación')
                                        : t('hairSalonCalendar.delete', 'Eliminar')}
                                </Button>
                            )}
                        </div>
                        <Button onClick={handleSave} disabled={!selectedService} type='button'>
                            {t('hairSalonCalendar.save', 'Guardar')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
